/**
 * Subscription API + Razorpay Checkout.
 *
 * The server decides what access a subscription grants; nothing here grants
 * anything locally. Checkout returns a signature that only the server can
 * validate, so activation always round-trips.
 */
import apiClient from './apiClient';

const CHECKOUT_SRC = 'https://checkout.razorpay.com/v1/checkout.js';

export async function fetchSubscription() {
  const res = await apiClient.get('/subscriptions/me');
  return res.data.data;
}

export async function startTrial() {
  const res = await apiClient.post('/subscriptions/trial');
  return res.data.data;
}

export async function cancelSubscription() {
  const res = await apiClient.post('/subscriptions/cancel');
  return res.data.data;
}

/** Loads Razorpay's script once; resolves immediately if already present. */
function loadCheckoutScript() {
  if (window.Razorpay) return Promise.resolve();
  const existing = document.querySelector(`script[src="${CHECKOUT_SRC}"]`);
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener('load', resolve);
      existing.addEventListener('error', () => reject(new Error('Could not load Razorpay.')));
    });
  }
  return new Promise((resolve, reject) => {
    const el = document.createElement('script');
    el.src = CHECKOUT_SRC;
    el.async = true;
    el.onload = resolve;
    el.onerror = () => reject(new Error('Could not load Razorpay. Check your connection.'));
    document.body.appendChild(el);
  });
}

/**
 * Opens Checkout for a plan and resolves with the server-confirmed state.
 * Rejects if the user dismisses the modal, so the caller can stop its spinner.
 */
export async function fetchInvoices() {
  const res = await apiClient.get('/subscriptions/invoices');
  return res.data.data || [];
}

/** Saves an invoice PDF; works for the owner and for admins. */
export async function downloadInvoice(invoice) {
  const res = await apiClient.get(`/subscriptions/invoices/${invoice.id}/pdf`, {
    responseType: 'blob',
  });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${String(invoice.invoiceNumber).replace(/\//g, '-')}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export async function subscribeToPlan(planCode, user, billing) {
  const res = await apiClient.post('/subscriptions/checkout', { planCode, billing });
  const checkout = res.data.data;

  await loadCheckoutScript();

  return new Promise((resolve, reject) => {
    const rzp = new window.Razorpay({
      key: checkout.keyId,
      subscription_id: checkout.subscriptionId,
      name: 'XLog3D',
      description: checkout.planName,
      prefill: {
        name: (user && user.name) || '',
        email: (user && user.email) || '',
      },
      notes: billing && billing.gstin ? { gstin: billing.gstin } : undefined,
      theme: { color: '#4caf50' },
      handler: async (response) => {
        try {
          const confirmed = await apiClient.post('/subscriptions/confirm', response);
          resolve(confirmed.data.data);
        } catch (err) {
          reject(err);
        }
      },
      modal: {
        // Exact wording matters: SubscriptionPage suppresses this one so
        // dismissing the modal is not reported as a failure.
        ondismiss: () => reject(new Error('Payment cancelled')),
      },
    });

    rzp.on('payment.failed', (resp) => {
      const d = (resp && resp.error && resp.error.description) || 'Payment failed.';
      reject(new Error(d));
    });

    rzp.open();
  });
}

export default {
  fetchSubscription,
  startTrial,
  cancelSubscription,
  subscribeToPlan,
  fetchInvoices,
  downloadInvoice,
};
